<template>
  <input
    ref="inputEl"
    type="text"
    inputmode="numeric"
    autocomplete="off"
    class="form-control"
    :value="display"
    :placeholder="placeholder"
    :disabled="disabled"
    :readonly="readonly"
    :id="inputId || undefined"
    @input="onInput"
    @focus="onFocus"
    @blur="onBlur"
  >
</template>

<script>
import { applyMoneyMask, formatMoney, parseMoney } from '@/utils/money.mjs';

// Campo de monto con separador de miles (RF-7). El `v-model` expone SIEMPRE un
// `Number` (o `null` si está vacío): es lo que hace que ningún cálculo aguas
// abajo —vuelto, pago mixto, diferencia de cierre— tenga que cambiar. El texto
// formateado es estado interno y no sale nunca del componente.
//
// Es `type="text"`, no `type="number"`, por necesidad y no por estética: un
// `input[type=number]` no admite separadores de miles y queda en estado
// inválido con cualquier carácter no numérico.
export default {
  name: 'MoneyInput',
  props: {
    modelValue: { type: [Number, String], default: null },
    placeholder: { type: String, default: '0' },
    disabled: { type: Boolean, default: false },
    readonly: { type: Boolean, default: false },
    decimals: { type: Number, default: 0 },
    inputId: { type: String, default: '' },
    // Cuando el campo representa un total que debe poder quedar en cero
    // explícito en vez de vacío.
    emitZeroWhenEmpty: { type: Boolean, default: false }
  },
  emits: ['update:modelValue', 'focus', 'blur'],
  data() {
    return { display: '' };
  },
  watch: {
    modelValue: {
      immediate: true,
      handler(value) {
        // No se reformatea mientras el usuario tipea: `onInput` ya dejó el
        // texto y el cursor donde corresponde, y volver a escribirlo acá los
        // pisaría.
        if (this.editing) return;
        this.display = value === null || value === undefined || value === ''
          ? ''
          : formatMoney(parseMoney(value), this.decimals);
      }
    }
  },
  created() {
    this.editing = false;
  },
  methods: {
    focus() {
      this.$refs.inputEl?.focus?.();
      this.$refs.inputEl?.select?.();
    },
    onInput(event) {
      const el = event.target;
      const { formatted, numeric, cursor } = applyMoneyMask(el.value, el.selectionStart, this.decimals);
      this.editing = true;
      this.display = formatted;
      el.value = formatted;
      el.setSelectionRange?.(cursor, cursor);
      this.$emit('update:modelValue', numeric === null && this.emitZeroWhenEmpty ? 0 : numeric);
      this.$nextTick(() => { this.editing = false; });
    },
    onFocus(event) {
      // Seleccionar todo al enfocar: tipear reemplaza en vez de concatenar.
      event.target.select?.();
      this.$emit('focus', event);
    },
    onBlur(event) {
      const numeric = parseMoney(this.display);
      this.display = numeric === null ? '' : formatMoney(numeric, this.decimals);
      this.$emit('blur', event);
    }
  }
};
</script>
